import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getColumns } from 'components/PotOrdersTable/columns';
import { printLabelCollection } from 'modules/print-label-collection/print-label-collection';
import ActionButton from 'components/ActionButton/ActionButton';
import { Dimmer, Icon, Loader, Message } from 'semantic-ui-react';
import updateOrdersStatuses from 'modules/update-orders-statuses/update-orders-statuses';
import { checkPrinter } from 'services/printer.service';
import { useAlert } from 'context/AlertProvider';
import { useZebra } from 'context/ZebraProvider';
import { getAll } from 'services/shipments.service';
import Portal from 'components/Portal/Portal';
import RequestManifest from 'components/RequestManifest/RequestManifest';
import VirtualTable from 'components/VirtualTable/VirtualTable';
import { getByShipment } from 'services/shipment-labels.service';
import { getMessageFromAxiosError } from 'modules/message-factory/message-factory';
import './pot-orders-table.scss';
import { STATUSES } from 'constants/constants';
import { useGroupId } from 'hooks/useGroupId';

export default function PotOrdersTable({ setIsPrinting, isPrinting }) {
  const [pendingOrders, setPendingOrders] = useState([]);
  const [fetchingOrders, setFetchingOrders] = useState(false);
  const printButtonRef = useRef();
  const alert = useAlert();
  const zebra = useZebra();
  const group_id = useGroupId();

  useEffect(() => {
    (async () => {
      if (!group_id) return;
      try {
        setFetchingOrders(true);
        const { data: orders } = await getAll({
          printed: false,
          status: {
            in: STATUSES.SHIPPED.name,
          },
          group_id,
        });
        const preparedOrders = orders.map((order) => ({
          ...order,
          status: 'pending',
        }));
        setPendingOrders(preparedOrders);
      } catch (error) {
        alert.current.error(getMessageFromAxiosError(error));
      } finally {
        setFetchingOrders(false);
      }
    })();
  }, [alert, group_id]);

  const data = useMemo(() => {
    return pendingOrders;
  }, [pendingOrders]);

  const printedReferences = useMemo(() => {
    return pendingOrders
      ?.filter((order) => order.status === 'printed')
      .map((order) => order.reference);
  }, [pendingOrders]);

  const columns = useMemo(() => getColumns(), []);

  const printNext = async (event) => {
    const currentOrder = pendingOrders?.find(
      (order) => order.status === 'pending',
    );

    if (!currentOrder) {
      setIsPrinting(false);

      return;
    }

    try {
      setIsPrinting(true);
      const isAvailable = zebra ? await checkPrinter(zebra) : false;
      if (!isAvailable) {
        alert.current.error(
          'Parece que la impresora por defecto no esta disponible, revise que esta encendida y conectada e intentelo de nuevo.',
        );
        setIsPrinting(false);

        return;
      }
      const updatedStatusesOrdersWaiting = updateOrdersStatuses(
        pendingOrders,
        currentOrder,
        'waiting',
      );
      setPendingOrders(updatedStatusesOrdersWaiting);

      try {
        const { data: labels } = await getByShipment(currentOrder.reference);
        const label_ids = labels.map((label) => label.id);
        await printLabelCollection(label_ids, currentOrder.reference, zebra);

        // success, status change: printed
        const updatedStatusesOrdersPrinted = updateOrdersStatuses(
          pendingOrders,
          currentOrder,
          'printed',
        );
        setPendingOrders(updatedStatusesOrdersPrinted);
      } catch (error) {
        const updatedStatusesOrdersFailed = updateOrdersStatuses(
          pendingOrders,
          currentOrder,
          'failed',
        );
        setPendingOrders(updatedStatusesOrdersFailed);
        alert.current.error(getMessageFromAxiosError(error));
      }

      // print next
      setTimeout(() => {
        if (printButtonRef) {
          printButtonRef?.current?.click();
        }
      }, 3000);
    } catch (error) {
      alert.current.error(getMessageFromAxiosError(error));
      // fail, status change: failed
      const updatedStatusesOrdersFailed = updateOrdersStatuses(
        pendingOrders,
        currentOrder,
        'failed',
      );
      setPendingOrders(updatedStatusesOrdersFailed);
      setIsPrinting(false);
    }
  };

  return (
    <section className="pot-orders">
      <label>
        Expediciones pendientes de imprimir
        <small>{pendingOrders.length} items</small>
      </label>
      <Dimmer active={fetchingOrders} inverted>
        <Loader inverted>Recuperando expediciones…</Loader>
      </Dimmer>
      {!!pendingOrders?.length ? (
        <>
          <VirtualTable data={data} columns={columns} />
          <ActionButton
            onClick={printNext}
            ref={printButtonRef}
            disabled={!pendingOrders.length}
          >
            {isPrinting ? (
              <>
                <Icon name="sync" loading />
                Imprimiendo
              </>
            ) : (
              <>Imprimir</>
            )}
          </ActionButton>
        </>
      ) : (
        <Message info>
          No hay expediciones pendientes de imprimir en este momento…
        </Message>
      )}
      {!!printedReferences?.length && (
        <Portal.In target="card-header">
          <RequestManifest shipments={printedReferences} />
        </Portal.In>
      )}
    </section>
  );
}
